// src/pages/Grow/Registration/[RegistrationDetails]/Tabs/ImportFromIntakeModal.tsx
/**
 * Copies the people staged on the originating course intake onto the registration.
 *
 * ⚠️ **The backend does the import** (`registration.importFromIntake`, SDK 0.0.58). This file only
 * picks *which* participants to send. The previous client-side version created each missing member
 * itself, which meant guessing a member **type**; the endpoint match-or-creates from the participant's
 * full details and is **idempotent**, so anyone already on the registration is skipped.
 *
 * ⚠️ Finding the intake is still two paths: `registration.intakeID` when the row has it, otherwise the
 * intake list's `registrationID` filter, since that column only populates rows created since 0.0.52.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Checkbox, Group, Loader, Modal, Stack, Text,
} from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle, IconUsers } from '@tabler/icons-react';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useRegistrationIntakeService, useRegistrationService,
  type RegistrationIntakeModelView,
} from '@actschurch/ccms-sdk';

interface ImportablePerson {
  key: string;
  /**
   * The participant id the endpoint addresses people by.
   *
   * ⚠️ Null for the applicant, who is derived from the intake's top-level fields rather than from
   * `participants[]`. They are imported with the endpoint's third argument (`includeApplicant`)
   * instead — see `handleImport`.
   */
  participantID: number | null;
  firstName: string;
  surname: string;
  email?: string | null;
  phone?: string | null;
  gender?: string | null;
  dateOfBirth?: string | null;
  isMain: boolean;
  memberID: number | null;
}

function peopleFromIntake(intake: RegistrationIntakeModelView): ImportablePerson[] {
  const participants = intake.participants ?? [];

  if (participants.length > 0) {
    return participants.map((p) => ({
      key: `participant-${p.participantID}`,
      participantID: p.participantID,
      firstName: (p.name ?? '').trim(),
      surname: (p.surname ?? '').trim(),
      email: p.email,
      phone: p.phone,
      gender: p.gender,
      dateOfBirth: p.dateOfBirth,
      isMain: p.isMain,
      memberID: p.memberID ?? null,
    }));
  }

  // No separate participants captured, so the applicant is the only person on the intake.
  return [{
    key: 'applicant',
    participantID: null,
    firstName: (intake.firstName ?? '').trim(),
    surname: (intake.surname ?? '').trim(),
    email: intake.email,
    phone: intake.phone,
    gender: intake.gender,
    isMain: true,
    memberID: intake.matchedMemberID ?? null,
  }];
}

const displayName = (p: ImportablePerson) =>
  [p.firstName, p.surname].filter(Boolean).join(' ').trim() || 'Unnamed';

export interface ImportFromIntakeModalProps {
  registrationID: number;
  /** The originating intake from `registration.intakeID`. Null on rows predating that field. */
  intakeID?: number | null;
  /** Member ids already on the registration, so people are not offered twice. */
  existingMemberIDs: number[];
  opened: boolean;
  onClose: () => void;
  onImported: () => void | Promise<void>;
}

export default function ImportFromIntakeModal({
  registrationID, intakeID, existingMemberIDs, opened, onClose, onImported,
}: ImportFromIntakeModalProps) {
  const intakeService = useRegistrationIntakeService();
  const registrationService = useRegistrationService();

  const [intake, setIntake] = useState<RegistrationIntakeModelView | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!opened) return;
    setLoading(true);
    setFailed(false);
    try {
      // Fast path: the registration knows its intake.
      if (intakeID != null) {
        setIntake(await intakeService.getById(intakeID));
        return;
      }

      // Fallback for rows predating `Registration.intakeID`: ask the intake list directly.
      const res = await intakeService.listView({
        registrationID: String(registrationID),
        skip: 0,
        take: 1,
      });
      const row = res?.items?.[0];
      if (!row) { setIntake(null); setFailed(true); return; }
      setIntake(await intakeService.getById(row.intakeID));
    } catch (e) {
      console.error('[ImportFromIntake] failed to resolve the intake:', e);
      setIntake(null);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [opened, intakeService, intakeID, registrationID]);

  useEffect(() => { load(); }, [load]);

  const people = useMemo(() => (intake ? peopleFromIntake(intake) : []), [intake]);

  const alreadyOn = useCallback(
    (p: ImportablePerson) => p.memberID != null && existingMemberIDs.includes(p.memberID),
    [existingMemberIDs],
  );
  const importable = useMemo(() => people.filter((p) => !alreadyOn(p)), [people, alreadyOn]);

  useEffect(() => {
    if (!opened) return;
    setSelected(Object.fromEntries(importable.map((p) => [p.key, true])));
  }, [opened, importable]);

  const chosen = importable.filter((p) => selected[p.key]);
  /** Rows the endpoint addresses by id. */
  const addressable = chosen.filter((p) => p.participantID != null);
  /**
   * Whether the applicant needs the `includeApplicant` flag.
   *
   * ⚠️ `peopleFromIntake` returns the applicant **only** when `participants[]` is empty, so the two
   * are mutually exclusive: a list is either all-addressable participants or the single applicant.
   * That is what makes it safe to omit the id array entirely below — there are no other participants
   * that "import everything missing" could sweep up.
   */
  const wantsApplicant = chosen.some((p) => p.participantID == null);
  const canImport = (addressable.length > 0 || wantsApplicant) && !busy;

  const handleImport = async () => {
    if (!canImport) return;
    setBusy(true);
    try {
      // ⚠️ Send ids explicitly when there are any — omitting them means "import everything missing",
      // which would ignore the checkboxes. The applicant has no id, so they ride on the third
      // argument; see `wantsApplicant` for why the two cases never overlap.
      const count = await registrationService.importFromIntake(
        registrationID,
        addressable.length > 0 ? addressable.map((p) => p.participantID as number) : undefined,
        wantsApplicant,
      );
      // ⚠️ Read through the envelope. `importFromIntake` is typed `Promise<number>`, but this API
      // returns `ResponseData`-wrapped payloads in places — and a bare `typeof === 'number'` check
      // reports 0 for a wrapped success, i.e. tells the admin nothing happened when it did.
      const envelope = count as unknown as { data?: number };
      const added = typeof count === 'number'
        ? count
        : typeof envelope?.data === 'number' ? envelope.data : 0;

      await onImported();

      if (added > 0) {
        toast.success(
          'Participants imported',
          `${added} added to the registration with their full details.`,
        );
        onClose();
      } else {
        // ⚠️ Do **not** report success here, and do **not** close. The endpoint is idempotent, so zero
        // legitimately means "already attached" — but it also means "the server accepted the request
        // and did nothing", which is indistinguishable from the client's side. Claiming an import that
        // the list will not reflect is how this looked broken in the first place.
        toast.warning(
          'Nothing was imported',
          'The server accepted the request but added nobody. They may already be on the '
          + 'registration, or the intake may have no participant record for them.',
        );
      }
    } catch (e) {
      console.error('[ImportFromIntake] import failed:', e);
      toast.error('Could not import', e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };



  return (
    <Modal
      opened={opened}
      onClose={busy ? () => {} : onClose}
      title="Import participants from the intake"
      size="lg"
      centered
    >
      {loading ? (
        <Stack align="center" gap="sm" py="xl">
          <Loader />
          <Text size="sm" c="dimmed">Finding the intake this registration came from…</Text>
        </Stack>
      ) : failed ? (
        <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">
            {intakeID != null
              ? `Could not load intake #${intakeID}. It may have been removed, or you may not have access to it.`
              : 'No intake points at this registration. It was most likely created by hand rather than from a public sign-up.'}
          </Text>
        </Alert>
      ) : (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            From {intake?.reference ?? `intake #${intake?.intakeID}`}
            {intake?.courseName ? ` · ${intake.courseName}` : ''}.
          </Text>

          <Stack gap="xs">
            {people.map((p) => {
              const on = alreadyOn(p);
              return (
                <Card key={p.key} withBorder radius="md" padding="sm">
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                      <Checkbox
                        checked={on ? true : Boolean(selected[p.key])}
                        disabled={on || busy}
                        onChange={(e) => setSelected((prev) => ({
                          ...prev, [p.key]: e.currentTarget.checked,
                        }))}
                      />
                      <div style={{ minWidth: 0 }}>
                        <Group gap={6}>
                          <Text size="sm" fw={600}>{displayName(p)}</Text>
                          {p.isMain && <Badge size="xs" variant="light" color="blue">Main</Badge>}
                        </Group>
                        <Text size="xs" c="dimmed" truncate>
                          {[p.email, p.phone].filter(Boolean).join(' · ') || 'No contact captured'}
                        </Text>
                      </div>
                    </Group>
                    <Badge
                      size="xs"
                      variant="light"
                      color={on ? 'gray' : p.memberID != null ? 'teal' : 'orange'}
                      style={{ flexShrink: 0 }}
                    >
                      {on
                        ? 'Already added'
                        : p.memberID != null ? `Member #${p.memberID}` : 'Will be created'}
                    </Badge>
                  </Group>
                </Card>
              );
            })}
          </Stack>



          {importable.length === 0 && (
            <Alert color="teal" variant="light" icon={<IconUsers size={16} />}>
              <Text size="sm">Everyone on this intake is already on the registration.</Text>
            </Alert>
          )}

          {people.length === 0 && (
            <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />}>
              <Text size="sm">This intake names nobody to import.</Text>
            </Alert>
          )}

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button onClick={handleImport} loading={busy} disabled={!canImport}>
              {chosen.length > 0 ? `Import ${chosen.length}` : 'Import'}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
